import { useEffect } from 'react'

import { IssueDetail } from './IssueDetail'

/**
 * The issue view as a CENTRED dialog, matching the create-issue modal.
 *
 * It used to slide over from the right. Opening a card therefore looked like a
 * different kind of surface from creating one, even though the two show the
 * same fields and the same actions — so editing felt like a separate mode
 * rather than the same form again. The board behind it is still untouched:
 * nothing here unmounts it, so scroll position and drag state survive exactly
 * as they did before.
 */
export function IssueDetailPanel({
  slug,
  issueRef,
  onClose,
  onOpenIssue,
}: {
  slug: string | null
  issueRef: string | null
  onClose: () => void
  onOpenIssue?: (ref: string) => void
}) {
  useEffect(() => {
    if (!issueRef) return
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape belongs to whatever is on top: a popover or the mention menu
      // stops the event before it reaches here.
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [issueRef, onClose])

  if (!issueRef) return null

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-6">
      <div
        className="absolute inset-0 bg-ink-950/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Issue ${issueRef}`}
        // max-h with a flex column, not a fixed height: the dialog grows to the
        // issue and stops at the viewport, and IssueDetail's own body is what
        // scrolls. A fixed height left a short issue floating in empty space.
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-2xl shadow-ink-950/20 dark:border-ink-800 dark:bg-ink-950"
      >
        <IssueDetail
          slug={slug}
          issueRef={issueRef}
          onClose={onClose}
          onOpenIssue={onOpenIssue}
          onDeleted={onClose}
        />
      </div>
    </div>
  )
}
